import { CategoryRounded, DeleteRounded, PeopleRounded } from "@mui/icons-material";
import InfoIcon from "@mui/icons-material/Info";
import {
  alpha,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialogs } from "../components/dialogs";
import PageHeader from "../components/page-header";
import StickyHeadTable, { Column } from "../components/table";
import { ManagedUser, UserStatusType } from "../types/general";
import { isValidEmail, normalizeEmail } from "../utils/validators";
import { ViewModelUsers } from "../viewmodel/viewmodel-users";

const STATUS_CHIP: Record<UserStatusType, { label: string; color: "warning" | "success" | "error" }> = {
  PENDENTE: { label: "Pendente", color: "warning" },
  CONFIRMADA: { label: "Confirmada", color: "success" },
  BLOQUEADA: { label: "Bloqueada", color: "error" },
};

const EMPTY_FORM = { name: "", email: "", phone: "", address: "" };

function UsersPage() {
  const theme = useTheme();
  const vm = ViewModelUsers();
  const navigate = useNavigate();

  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState("");

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setFormError("");
    setCreateOpen(true);
  };

  const handleCreate = async () => {
    const name = form.name.trim();
    if (!/^\S+\s+\S+.+$/.test(name)) {
      setFormError("Por favor, forneça pelo menos dois nomes.");
      return;
    }
    if (!isValidEmail(form.email)) {
      setFormError("Insira um email válido.");
      return;
    }
    if (form.phone.trim() && !/^8[2-7]\d{7}$/.test(form.phone.trim())) {
      setFormError("O número de telefone deve ter 9 dígitos e começar por 82-87.");
      return;
    }
    const created = await vm.createUser({
      name,
      email_whatsapp: normalizeEmail(form.email),
      phone: form.phone.trim() || undefined,
      address: form.address.trim() || undefined,
    });
    if (created) setCreateOpen(false);
  };

  const columns: Column<ManagedUser>[] = [
    { id: "name", label: "Nome", minWidth: 180 },
    { id: "email_whatsapp", label: "Email", minWidth: 200 },
    { id: "phone", label: "Telefone", minWidth: 110, format: (v) => v || "—" },
    {
      id: "status",
      label: "Estado",
      minWidth: 110,
      renderCell: (row) => {
        const chip = STATUS_CHIP[row.status] ?? { label: row.status, color: "default" as any };
        return <Chip label={chip.label} color={chip.color} size="small" />;
      },
    },
    {
      id: "access",
      label: "Perfil",
      minWidth: 110,
      renderCell: (row) => (row.access === "ADMIN" ? "Administrador" : "Utilizador"),
    },
    {
      id: "temas",
      label: "Temas",
      minWidth: 90,
      renderCell: (row) => row.temas.length,
    },
    {
      id: "actions",
      label: "Ações",
      minWidth: 170,
      renderCell: (row) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Button size="small" startIcon={<CategoryRounded />} onClick={() => navigate(`/utilizadores/${row.id}/temas`)}>
            Temas
          </Button>
          <Tooltip title="Remover" arrow>
            <IconButton size="small" color="error" onClick={() => vm.confirmDelete(row)}>
              <DeleteRounded fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 2 }}>
      <PageHeader
        icon={<PeopleRounded />}
        color="#8B5CF6"
        title="Gestão de Utilizadores"
        actions={
          <Button variant="contained" onClick={openCreate}>
            Novo Utilizador
          </Button>
        }
      />

      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 3,
          background: alpha(theme.palette.info.main, 0.06),
          border: `1px solid ${alpha(theme.palette.info.main, 0.25)}`,
          borderRadius: 3,
          display: "flex",
          alignItems: "center",
          gap: 2,
        }}
      >
        <Box
          sx={{
            backgroundColor: theme.palette.info.main,
            borderRadius: "50%",
            width: 40,
            height: 40,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <InfoIcon sx={{ color: "white", fontSize: 24 }} />
        </Box>
        <Box flex={1}>
          <Typography variant="subtitle2" fontWeight="bold" color="info.main">
            Como funciona
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Os utilizadores criados ficam <b>pendentes</b> com a senha padrão <b>000000</b>. Ao tentar entrar,
            recebem um código por email e definem a sua nova senha.
          </Typography>
        </Box>
      </Paper>

      <StickyHeadTable
        columns={columns}
        rows={vm.users}
        getRowId={(row) => row.id}
        pagination={{
          totalData: vm.pagination.totalData,
          totalPages: vm.pagination.totalPages,
          currentPage: vm.pagination.currentPage,
          onPageChange: (page) => vm.fetchUsers(page),
          onRowsPerPageChange: () => {},
        }}
      />

      {/* Criar utilizador */}
      <Dialog open={createOpen} onClose={() => !vm.loading && setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Novo Utilizador</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "8px !important" }}>
          <TextField
            autoFocus
            label="Nome completo"
            fullWidth
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            disabled={vm.loading}
          />
          <TextField
            label="Email"
            type="email"
            fullWidth
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            disabled={vm.loading}
          />
          <TextField
            label="Telefone (opcional)"
            fullWidth
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            inputProps={{ inputMode: "numeric", maxLength: 9 }}
            disabled={vm.loading}
          />
          <TextField
            label="Endereço (opcional)"
            fullWidth
            value={form.address}
            onChange={(e) => setForm({ ...form, address: e.target.value })}
            inputProps={{ maxLength: 50 }}
            disabled={vm.loading}
          />
          {formError && (
            <Typography variant="body2" color="error">
              {formError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} disabled={vm.loading}>
            Cancelar
          </Button>
          <Button onClick={handleCreate} variant="contained" disabled={vm.loading}>
            {vm.loading ? <CircularProgress size={22} color="inherit" /> : "Criar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialogs {...vm.dialog} />
    </Container>
  );
}

export default UsersPage;
